import React, { forwardRef } from 'react';
import { Box, Typography, Grid, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import ReportPrintHeader from '../../components/ReportPrintHeader';

const VoucherPrint = forwardRef(({ voucher, companyInfo }, ref) => {
  if (!voucher) return null;

  // Helper to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Helper to get voucher type name
  const getVoucherTypeName = (type) => {
    // Check if type is numeric or string and match
    const typeNum = Number(type);
    const types = {
      101: 'سند قبض من عميل',
      102: 'سند صرف لمورد',
      103: 'سند قبض عام',
      104: 'سند صرف عام',
      105: 'سند صرف ضريبة'
    };
    return types[typeNum] || 'سند';
  };

  // Helper for Payment Method
  const getPaymentMethod = () => {
    // Assuming 0 or 1 for Cash/Bank based on form state logic usually
    // But voucher object might have paymentType as 1 (Cash) or 2 (Bank) based on backend
    // Let's handle both common conventions or check what's passed
    // In TaxPaymentForm: paymentType: form.paymentType === 0 ? 1 : 2
    if (voucher.paymentType === 1 || voucher.paymentType === 0) return 'نقدي'; // 1 is Cash in backend usually
    if (voucher.paymentType === 2 || voucher.paymentType === 1) return 'بنكي'; // 2 is Bank
    return 'غير محدد';
  };

  const isReceipt = [101, 103].includes(Number(voucher.voucherType));

  return (
    <Box ref={ref} sx={{ p: 4, bgcolor: '#fff', direction: 'rtl', minHeight: '100vh' }} className="print-container" dir="rtl">
      <style>{`
        @media print {
          .print-container {
             direction: rtl !important;
          }
          .MuiGrid-root {
             direction: rtl !important;
          }
          .MuiTypography-root {
             text-align: right;
          }
        }
      `}</style>
      
      {/* Header */}
      <ReportPrintHeader 
        title={getVoucherTypeName(voucher.voucherType)}
        subTitle={`رقم السند: ${voucher.voucherNumber}`}
        company={companyInfo}
      />

      {/* Main Voucher Info Box */}
      <Box sx={{ border: '2px solid #000', borderRadius: 2, mb: 4, p: 2, direction: 'rtl' }}>
        <Grid container spacing={2} dir="rtl">
            {/* Row 1 */}
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                    <Typography fontWeight="bold" width="100px" textAlign="right">رقم السند:</Typography>
                    <Typography variant="h6" fontWeight="bold">{voucher.voucherNumber}</Typography>
                </Box>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                    <Typography fontWeight="bold" width="100px" textAlign="right">التاريخ:</Typography>
                    <Typography variant="h6" fontWeight="bold" dir="ltr">{voucher.voucherDate ? new Date(voucher.voucherDate).toLocaleDateString('en-GB') : '-'}</Typography>
                </Box>
            </Grid>
            
            <Grid item xs={12}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

            {/* Row 2 */}
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                    <Typography fontWeight="bold" width="100px" textAlign="right">طريقة الدفع:</Typography>
                    <Typography>{getPaymentMethod()}</Typography>
                </Box>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                {voucher.referenceNumber && (
                    <Box display="flex" alignItems="center" justifyContent="flex-start">
                        <Typography fontWeight="bold" width="100px" textAlign="right">المرجع:</Typography>
                        <Typography>{voucher.referenceNumber}</Typography>
                    </Box>
                )}
            </Grid>

             <Grid item xs={12}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

             {/* Row 3: Account/Party */}
             <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                    <Typography fontWeight="bold" width="100px" textAlign="right">
                        {isReceipt ? 'استلمنا من:' : 'صرفنا إلى:'}
                    </Typography>
                    <Typography variant="h6" sx={{ borderBottom: '1px dotted #000', flex: 1, px: 1, textAlign: 'right' }}>
                        {voucher.supplierName || voucher.customerName || voucher.accountName || '—'}
                    </Typography>
                </Box>
             </Grid>

             {/* Row 4: Amount */}
             <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Box display="flex" alignItems="center" mt={2} justifyContent="flex-start">
                    <Typography fontWeight="bold" width="100px" textAlign="right">مبلغ وقدره:</Typography>
                    <Box sx={{ 
                        bgcolor: '#f5f5f5', 
                        border: '1px solid #000', 
                        borderRadius: 1, 
                        px: 3, 
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        direction: 'rtl'
                    }}>
                        {/* In RTL: First item is Rightmost. We want Number then Currency? Or Currency then Number? 
                            Usually: 500 SAR. 
                            Right: 500. Left: SAR.
                        */}
                        <Typography variant="h5" fontWeight="bold" dir="ltr">{formatCurrency(voucher.amount)}</Typography>
                        <Typography variant="body1" fontWeight="bold">SAR</Typography>
                    </Box>
                </Box>
             </Grid>
        </Grid>
      </Box>

      {/* Description / Notes */}
      <Box sx={{ mb: 4, direction: 'rtl', textAlign: 'right' }}>
        <Typography fontWeight="bold" gutterBottom textAlign="right">وذلك عن:</Typography>
        <Box sx={{ 
            border: '1px solid #ccc', 
            borderRadius: 2, 
            p: 2, 
            minHeight: '80px', 
            bgcolor: '#fafafa',
            textAlign: 'right'
        }}>
            <Typography textAlign="right">{voucher.description || voucher.notes || '-'}</Typography>
        </Box>
      </Box>

      {/* Bank/Safe Details if available */}
      {(voucher.bankName || voucher.safeName) && (
        <Box sx={{ mb: 4, p: 2, border: '1px dashed #ccc', borderRadius: 2, direction: 'rtl' }}>
            <Grid container spacing={2} dir="rtl">
                {voucher.safeName && (
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary" textAlign="right">الخزنة: <span style={{color:'black', fontWeight:'bold'}}>{voucher.safeName}</span></Typography>
                    </Grid>
                )}
                {voucher.bankName && (
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                         <Typography variant="body2" color="text.secondary" textAlign="right">البنك: <span style={{color:'black', fontWeight:'bold'}}>{voucher.bankName}</span></Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
      )}

      {/* Signatures */}
      <Box sx={{ mt: 10 }}>
        <Grid container spacing={4} justifyContent="center" textAlign="center">
            <Grid item xs={4}>
                <Typography fontWeight="bold" gutterBottom>المحاسب</Typography>
                <Box sx={{ mt: 4, borderBottom: '1px solid #000', width: '80%', mx: 'auto' }}></Box>
            </Grid>
            <Grid item xs={4}>
                <Typography fontWeight="bold" gutterBottom>المدير المالي</Typography>
                <Box sx={{ mt: 4, borderBottom: '1px solid #000', width: '80%', mx: 'auto' }}></Box>
            </Grid>
            <Grid item xs={4}>
                <Typography fontWeight="bold" gutterBottom>المستلم</Typography>
                <Box sx={{ mt: 4, borderBottom: '1px solid #000', width: '80%', mx: 'auto' }}></Box>
            </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <div className="report-footer">
        <Typography variant="caption">
            تمت الطباعة بواسطة نظام بروفي إي آر بي | ProfiERP System
        </Typography>
      </div>
      
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; }
          .report-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            text-align: center;
            font-size: 11px;
            color: #777;
            border-top: 1px solid #ddd;
            padding-top: 4px;
            background: white;
          }
        }
      `}</style>
    </Box>
  );
});

export default VoucherPrint;
